import { Select, Spin } from 'antd';
import React, { useMemo, useState, useEffect } from 'react';
import { useGetPatientIdsQuery } from '@ui/app/api';
import type { IngestionFilterCondition } from '../../types';

interface IngestionPatientIdSelectProps {
    condition: IngestionFilterCondition;
    groupId: string;
    updateCondition: (groupId: string, conditionId: string, updates: Partial<IngestionFilterCondition>) => void;
}

/**
 * Multi-select for patient IDs in ingestion filter
 * Fetches unique patient IDs from Elasticsearch
 */
export const IngestionPatientIdSelect: React.FC<IngestionPatientIdSelectProps> = ({
    condition,
    groupId,
    updateCondition,
}) => {
    const [patientIdSearch, setPatientIdSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(patientIdSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [patientIdSearch]);

    const { data, isLoading: isLoadingPatientIds } = useGetPatientIdsQuery({
        search: debouncedSearch,
        limit: 100,
    });

    const patientIds = data?.patientIds || [];

    const patientIdOptions = useMemo(() => {
        const options = patientIds.map(id => ({
            label: String(id),
            value: id,
        }));

        // Ensure currently selected values are always in the options
        const currentValues = Array.isArray(condition.value)
            ? condition.value
            : condition.value
              ? [condition.value]
              : [];
        currentValues.forEach(val => {
            if (!options.find(opt => opt.value === val)) {
                options.push({ label: String(val), value: val });
            }
        });

        return options;
    }, [patientIds, condition.value]);

    const handleChange = (vals: (string | number)[] | undefined) => {
        updateCondition(groupId, condition.id, {
            value: vals && vals.length > 0 ? vals : undefined,
        });
    };

    return (
        <div className="flex w-full flex-col gap-2">
            <Select
                mode="multiple"
                placeholder="Search or select Patient IDs..."
                className="w-full"
                allowClear
                showSearch
                filterOption={true}
                onSearch={setPatientIdSearch}
                options={patientIdOptions}
                loading={isLoadingPatientIds}
                value={condition.value}
                onChange={handleChange}
                notFoundContent={isLoadingPatientIds ? <Spin size="small" /> : 'No patient IDs found'}
                maxTagCount="responsive"
            />
        </div>
    );
};
